import { normalizePathname, ROUTES } from "@/lib/routes";

export const THEME_STORAGE_KEY = "bb-theme";

export type Theme = "light" | "dark" | "system";

let forcedLightCount = 0;

function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function resolveTheme(theme: Theme): "light" | "dark" {
  return theme === "system" ? getSystemTheme() : theme;
}

export function isHomePath(pathname: string): boolean {
  return normalizePathname(pathname.split("?")[0] ?? pathname) === ROUTES.home;
}

export function resolveDisplayedTheme(
  theme: Theme,
  pathname: string,
  options?: { forceLight?: boolean },
): "light" | "dark" {
  if (options?.forceLight || isHomePath(pathname)) {
    return "light";
  }
  return resolveTheme(theme);
}

export function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

export function applyThemeClass(theme: Theme) {
  const pathname =
    typeof window === "undefined" ? ROUTES.home : window.location.pathname;
  const resolved = resolveDisplayedTheme(theme, pathname, {
    forceLight: forcedLightCount > 0,
  });
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
  root.dataset.theme = theme;
}

/** Keep the landing screen light without changing the stored preference. */
export function acquireForcedLightTheme(): () => void {
  forcedLightCount += 1;
  applyThemeClass(readStoredTheme());
  return () => {
    forcedLightCount = Math.max(0, forcedLightCount - 1);
    applyThemeClass(readStoredTheme());
  };
}
